import {
  Body,
  BodyProps,
  Box,
  BoxProps,
  ButtCon,
  Button,
  Card,
  Heading,
  Icon,
  ProgressBar,
  ResponsiveSizes,
  TextInput,
  Tooltip
} from '@biom3/react';
import { StockStatusEnum } from 'lib/woocomerce/__generated__/graphql';
import { Product, ProductCollectionType } from 'lib/woocomerce/types';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { CardFrame } from 'src/components/product-card/card-frame';
import { useProductsContext } from 'src/context/products-context';
import { useStore } from 'src/context/store-context';
import { useConnectWidget } from 'src/hooks/use-connect-widget';
import { useQuantity } from 'src/hooks/use-quantity';
import { toFormatedCurrency } from 'src/utils/misc';
import { getProductAttributes } from '../../utils/product-attributes';
import CountdownTimer from '../count-down-timer';

export type ProductCardProps = {
  product: Product;
  onClick: (product: Product, qty: number) => void; // eslint-disable-line no-unused-vars
};

const StockStatus = ({ stockQty, product }: { product: Product; stockQty: number }) => {
  const [{ saleEnded }] = useStore();

  const unlimitedStock =
    product.stockQuantity === null && product.stockStatus === StockStatusEnum.InStock;
  const stockLabelA = unlimitedStock ? 'In stock' : `${stockQty} remaining`;
  const stockLabelB = stockQty > 0 ? stockLabelA : 'Out of stock';
  const label = saleEnded ? 'sale ended' : stockLabelB;

  return (
    <Box
      sx={{
        borderRadius: 'base.spacing.x1',
        backgroundColor: 'base.color.translucent.emphasis.300',
        px: 'base.spacing.x4',
        py: 'base.spacing.x2',
        userSelect: 'none'
      }}
    >
      {label}
    </Box>
  );
};

const NotTradableTag = () => {
  return (
    <Tooltip>
      <Tooltip.Target>
        <Body
          size="small"
          weight="bold"
          sx={{
            px: 'base.spacing.x1',
            brad: 'base.borderRadius.x1',
            color: 'base.color.text.body.inverse.primary',
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            userSelect: 'none',
            bg: 'base.color.accent.8',
            '&:hover': {
              filter: 'brightness(0.9)'
            }
          }}
        >
          Not tradable
          <Icon
            icon="InformationCircle"
            sx={{
              ml: 'base.spacing.x1',
              fill: 'inherit',
              width: 'base.spacing.x4',
              height: 'base.spacing.x4'
            }}
          />
        </Body>
      </Tooltip.Target>
      <Tooltip.Content
        sx={{
          maxw: {
            default: '200px',
            medium: '370px',
            small: '300px'
          }
        }}
      >
        <b>These are not NFT’s and will not show up in your wallet. </b>
        We recommend buying in-game assets with Passport.
      </Tooltip.Content>
    </Tooltip>
  );
};

const Title = ({ product, tradable }: { product: Product; tradable: boolean }) => {
  return (
    <Box sx={{ paddingBottom: 'base.spacing.x2', display: 'flex', alignItems: 'center' }}>
      <Heading size="small" sx={{ userSelect: 'none', mr: 'base.spacing.x2' }}>
        {product.name}
      </Heading>
      {!tradable ? <NotTradableTag /> : undefined}
    </Box>
  );
};

const CurrencyDisplay = ({ name, size }: { name: string; size: BodyProps['size'] }) => {
  const symbol = name.toLowerCase().replace(/^t|w(\w+)$/gm, '$1');
  const symbolURL = `/images/currency--${symbol}.svg`;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center'
      }}
    >
      <Box
        rc={<Image width={16} height={16} src={symbolURL} alt={name} />}
        sx={{
          mr: 'base.spacing.x1'
        }}
      />
      <Body size={size} weight="regular" sx={{ pr: 'base.spacing.x1', color: 'inherit' }}>
        {name}
      </Body>
    </Box>
  );
};

const Price = ({
  price,
  markdownPrice,
  discounted,
  currency,
  highlighted
}: {
  price: number;
  markdownPrice: number;
  discounted: boolean;
  currency: string;
  highlighted: boolean;
}) => {
  const copySize: BodyProps['size'] = highlighted
    ? ['medium', 'medium', 'large', 'medium', 'large']
    : ['small', 'small', 'medium', 'small', 'medium'];
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          color: highlighted ? 'base.color.text.body.primary' : 'base.color.text.body.secondary'
        }}
      >
        <CurrencyDisplay name={currency} size={copySize} />
        <Body size={copySize} weight="bold" sx={{ color: 'inherit' }}>
          {toFormatedCurrency(price)}
        </Body>
        {discounted && (
          <Body
            sx={{
              px: 'base.spacing.x2',
              textDecoration: 'base.font.decoration.lineThrough',
              color: 'base.color.text.body.secondary'
            }}
            size={copySize}
          >
            &nbsp;{toFormatedCurrency(markdownPrice)}&nbsp;
          </Body>
        )}
      </Box>
    </Box>
  );
};

const Pricing = ({
  product,
  quantity,
  bundleSize
}: {
  product: Product;
  quantity: number;
  bundleSize: number;
}) => {
  const discounted = product.pricing.regularPrice !== product.pricing.price;

  const prices = [
    {
      price: product.pricing.price,
      currency: product.pricing.currency,
      regularPrice: product.pricing.regularPrice,
      salePrice: product.pricing.salePrice
    },
    ...(product.pricing.alternateCurrencies || [])
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'flex-start'
      }}
    >
      {prices.map(({ price: basePrice, currency, regularPrice, salePrice }, index) => {
        const comparisonPrice = Number(
          basePrice === salePrice ? regularPrice : salePrice || regularPrice
        );

        const price = Number(basePrice) * quantity * bundleSize;

        const markdownPrice = comparisonPrice * quantity * bundleSize;

        return (
          <Price
            key={currency}
            price={Number(price)}
            currency={currency}
            discounted={discounted}
            markdownPrice={markdownPrice}
            highlighted={index === 0}
          />
        );
      })}
    </Box>
  );
};

const StoreDiscount = ({
  product,
  bundleSize,
  discount = 0
}: {
  product: Product;
  bundleSize: number;
  discount: number;
}) => {
  const { salePrice, regularPrice } = product.pricing;
  const extraDiscount = +salePrice !== 0;
  const retailPrice = Number(regularPrice) * bundleSize;
  const groupDiscount = product?.pricing?.groupDiscountPercentage;
  const totalDiscount = Math.max(groupDiscount, discount);
  const isGroupDiscount = product.pricing?.isGroupDiscountApplied;
  const groupDiscountAvailable = product.pricing?.numberOfDiscountPurchaseLeft;
  if (!totalDiscount)
    return <Box sx={{ display: 'flex', width: '100%', mb: 'base.spacing.x10' }} />;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%',
        justifyContent: 'flex-start',
        mb: !totalDiscount ? 'base.spacing.x10' : 'base.spacing.x3'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          px: 'base.spacing.x2',
          py: 'base.spacing.x1',
          brad: 'base.borderRadius.x1',
          bg: 'base.color.brand.1'
        }}
      >
        <Body
          size="small"
          weight="bold"
          sx={{
            color: 'base.color.text.body.inverse.primary'
          }}
        >
          {isGroupDiscount && totalDiscount === groupDiscount ? (
            <Tooltip>
              <Tooltip.Target>
                {extraDiscount && 'Extra'} {totalDiscount}% off applied | {groupDiscountAvailable}{' '}
                Remaining
              </Tooltip.Target>
              <Tooltip.Content>
                Your wallet has {groupDiscountAvailable} purchase
                {groupDiscountAvailable > 1 && 's'} with a discount
              </Tooltip.Content>{' '}
            </Tooltip>
          ) : (
            <>
              {extraDiscount && 'Extra'} {totalDiscount}% off applied
            </>
          )}
        </Body>
      </Box>

      {extraDiscount && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            px: 'base.spacing.x2',
            py: 'base.spacing.x1',
            brad: 'base.borderRadius.x1',
            bg: 'base.color.brand.1',
            ml: 'base.spacing.x2'
          }}
        >
          <Body
            size="small"
            weight="bold"
            sx={{
              color: 'base.color.text.body.inverse.primary'
            }}
          >
            RRP {toFormatedCurrency(retailPrice)}
          </Body>
        </Box>
      )}
    </Box>
  );
};

const QuantityInput = ({
  quantity,
  maxQuantity,
  setQuantity,
  bundleSize,
  bundleQuantity
}: {
  quantity: number;
  bundleSize: number;
  bundleQuantity: number;
  maxQuantity: number;
  setQuantity: (qty: number) => void; // eslint-disable-line no-unused-vars
}) => {
  const [inputQty, setInputQty] = useState(bundleQuantity);
  const [focused, setFocused] = useState(false);

  const maxQty = Math.floor(maxQuantity / (bundleSize || 1));

  useEffect(() => {
    setInputQty(bundleQuantity);
  }, [bundleQuantity]);

  if (!focused) {
    return (
      <Body
        size="medium"
        weight="bold"
        onClick={() => setFocused(true)}
        sx={{
          px: 'base.spacing.x2',
          cursor: 'pointer',
          mx: 'base.spacing.x1',
          '&:hover': {
            color: 'base.color.text.body.primary',
            borderRadius: 'base.borderRadius.x4',
            outlineStyle: 'solid',
            outlineColor: 'base.color.text.body.secondary',
            outlineWidth: 'base.border.size.100',
            outlineOffset: '-4px',
            bg: 'base.color.translucent.emphasis.300',
            py: 'base.spacing.x2'
          }
        }}
      >
        {bundleQuantity}
      </Body>
    );
  }

  const valid = inputQty !== bundleQuantity && inputQty > 0 && inputQty <= maxQty;

  const onDone = () => {
    setFocused(false);

    if (valid) {
      const newQty = inputQty * bundleSize;
      const deltaQty = Math.abs(quantity - newQty) * (bundleQuantity > inputQty ? -1 : 1);
      setQuantity(deltaQty);
    }
    setInputQty(inputQty);
  };

  return (
    <Body size="medium" weight="bold" sx={{ px: 'base.spacing.x2' }}>
      <TextInput
        autoFocus
        sizeVariant="medium"
        value={inputQty}
        hideClearValueButton
        onChange={(e) => {
          const value = Number(e.target.value);
          if (!isNaN(value) && value >= 0 && value <= maxQty) {
            setInputQty(value);
          }
        }}
        onBlur={onDone}
        sx={{ width: '100%' }}
      >
        <TextInput.StatefulButtCon
          icon={valid ? 'Tick' : 'Close'}
          state="initial"
          sx={{
            minWidth: 'auto',
            width: 'base.spacing.x6',
            height: 'base.spacing.x6'
          }}
        />
      </TextInput>
    </Body>
  );
};

const Quantity = ({
  // stockQty,
  quantity,
  bundleSize,
  setQuantity,
  maxQuantity,
  notAvailable,
  exceedQuantity
}: {
  product: Product;
  quantity: number;
  bundleSize: number;
  maxQuantity: number;
  notAvailable: boolean;
  exceedQuantity: boolean;
  setQuantity: (qty: number) => void; // eslint-disable-line no-unused-vars
}) => {
  // const { allowlisted } = useProductsContext();
  const bundleQuantity = Math.floor(quantity / (bundleSize || 1));
  // const willExceedMaxQuantity = quantity + bundleSize > maxQuantity;
  // const overMaxQuantity = bundleQuantity === 1 && (exceededQuantity || willExceedMaxQuantity);
  //const notAvailable = stockQty <= 0 || overMaxQuantity || !allowlisted;

  if (notAvailable) {
    return;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1 / 2,
        alignItems: 'center',
        justifyContent: 'space-between',
        marginRight: 'base.spacing.x4',
        marginBottom: 'base.spacing.x3',
        width: '125px'
      }}
    >
      <ButtCon icon="Minus" variant="tertiary" onClick={() => setQuantity(-1 * bundleSize)} />
      <QuantityInput
        quantity={quantity}
        bundleQuantity={bundleQuantity}
        setQuantity={setQuantity}
        bundleSize={bundleSize}
        maxQuantity={maxQuantity * bundleSize}
      />
      <ButtCon
        icon="Add"
        variant="tertiary"
        disabled={exceedQuantity || quantity >= maxQuantity * bundleSize}
        onClick={() => setQuantity(+1 * bundleSize)}
      />
    </Box>
  );
};

const htmlDescriptionStyles: BoxProps['sx'] = {
  a: {
    color: 'base.color.text.link.primary'
  },
  'a:hover': {
    textDecoration: 'base.font.decoration.underline'
  }
};

const HtmlDescription = ({ html }: { html: string }) => {
  return (
    <Box
      sx={{
        ...htmlDescriptionStyles,
        paddingBottom: 'base.spacing.x8',
        userSelect: 'none',
        flex: 'auto'
      }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

const Description = ({ product }: { product: Product }) => {
  return <HtmlDescription html={product.description} />;
};

const BuyNow = (props: {
  product: Product;
  onClick: () => void;
  stockQty: number;
  notAvailable: boolean;
  isLimited: boolean;
}) => {
  const { stockQty, onClick, product, notAvailable, isLimited } = props;
  const [{ saleEnded }] = useStore();
  const { connectWallet, connected } = useConnectWidget();

  const inStock = product.stockStatus === StockStatusEnum.InStock;
  const outOfStock = stockQty <= 0 && !inStock;

  if (notAvailable) {
    let label = '';
    if (saleEnded) {
      label = 'Pre-sale has finished';
    }
    // else if (!allowlisted) {
    //   label = 'Sorry this item is unavailable';
    // }
    else if (outOfStock) {
      label = 'Sold out';
    }

    return (
      <Body
        weight="bold"
        size="medium"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          backgroundColor: 'base.color.translucent.emphasis.300',
          borderRadius: 'base.spacing.x1',
          px: 'base.spacing.x2',
          py: 'base.spacing.x4'
        }}
      >
        {label}
      </Body>
    );
  }

  const handleOnClick = () => {
    if (connected) {
      onClick();
      return;
    }

    connectWallet();
  };

  if (isLimited) {
    return (
      <Button
        sx={{
          flex: 1,
          whiteSpace: 'nowrap',
          backgroundColor: 'base.color.translucent.emphasis.300',
          marginBottom: 'base.spacing.x3'
        }}
        disabled
        onClick={handleOnClick}
      >
        Buy now
      </Button>
    );
  }

  return (
    <Button
      sx={{
        flex: 1,
        whiteSpace: 'nowrap',
        marginBottom: 'base.spacing.x3'
      }}
      onClick={handleOnClick}
    >
      Buy now
    </Button>
  );
};

const AvailableQuantity = ({
  availableQuantity,
  limitQuantity
}: {
  availableQuantity: string;
  limitQuantity: string;
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'flex-end'
      }}
    >
      <>
        Available {availableQuantity}/{limitQuantity}
      </>
    </Box>
  );
};

const CountdownRefreshTime = ({
  countdownTime,
  onRefresh
}: {
  countdownTime: string;
  onRefresh: () => void;
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'flex-end'
      }}
    >
      <>
        Refreshes in:&nbsp;
        <CountdownTimer countDownTime={countdownTime} onRefresh={onRefresh} />
      </>
    </Box>
  );
};

const WalletLimitInfomation = ({
  availableQuantity,
  limitQuantity,
  countdownTime,
  onRefresh
}: {
  availableQuantity?: string;
  limitQuantity?: string;
  countdownTime: string;
  onRefresh: () => void;
}) => {
  if (!countdownTime && !limitQuantity) {
    return null;
  }
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'end',
        justifyContent: 'end'
      }}
    >
      <Box sx={{ width: '125px', marginRight: 'base.spacing.x4' }} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flex: 1
        }}
      >
        {availableQuantity && limitQuantity ? (
          <AvailableQuantity availableQuantity={availableQuantity} limitQuantity={limitQuantity} />
        ) : null}
        {countdownTime ? (
          <>
            <CountdownRefreshTime countdownTime={countdownTime} onRefresh={onRefresh} />
          </>
        ) : null}
      </Box>
    </Box>
  );
};

export const ProductCard = ({ product, onClick }: ProductCardProps) => {
  const { maxQuantity, bundleSize, stockQty, attributes } = getProductAttributes(product);
  const [productQty, setProductQty, exceedQuantity, resetQuantity] = useQuantity(
    bundleSize,
    maxQuantity
  );
  const [{ saleEnded }] = useStore();
  const { setHasDiscount, hasDiscount } = useProductsContext();
  const [progress, setProgress] = useState<number>(0);
  const [discount, setDiscount] = useState<number>(0);
  const [showDiscount, setShowdiscount] = useState<boolean>(false);

  const bondingCurve = product.pricing?.bondingCurve;
  const discountCurve = product.pricing?.discountCurve;
  const inStock = product.stockStatus === StockStatusEnum.InStock;
  const outOfStock = stockQty <= 0 && !inStock;
  const notAvailable = outOfStock || saleEnded;

  const [isLimited, setIsLimited] = useState<boolean>(false);
  const [available, setAvailable] = useState<string>('');
  const [limit, setLimit] = useState<string>('');
  const [countdown, setCountdown] = useState('');

  const availableBundle = available ? parseInt(available) / bundleSize : null;
  const limitBundle = limit ? parseInt(limit) / bundleSize : null;

  const refreshCountdown = () => {
    setIsLimited(false);
    setCountdown(product?.walletLimit?.countdownTime || `${24 * 60 * 60}`);
    setAvailable(product?.walletLimit?.availableQuantity || limit);
  };

  useEffect(() => {
    setIsLimited(product?.walletLimit?.isLimited);
    setAvailable(product?.walletLimit?.availableQuantity);
    setLimit(product?.walletLimit?.limitQuantity);
    setCountdown(product?.walletLimit?.countdownTime);
    resetQuantity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  useEffect(() => {
    if (!bondingCurve) {
      return;
    }
    const remainingDiscountPool = bondingCurve?.remainingDiscountPool ?? 0;
    const percentage = product.pricing?.totalDiscountPercentage ?? 0;

    if (bondingCurve) {
      const currentProgress = Number(remainingDiscountPool);

      if (currentProgress >= 0 && currentProgress <= 100) {
        setProgress(Number(currentProgress.toFixed(2)));
      }

      setDiscount(percentage ?? 0);
    }
  }, [bondingCurve, product, setHasDiscount, hasDiscount]);

  useEffect(() => {
    if (!discountCurve) {
      return;
    }
    setHasDiscount(true);
    const remainingDiscountPool = discountCurve?.remainingDiscountPool ?? 0;
    const percentage = product.pricing?.totalDiscountPercentage ?? 0;
    const currentProgress = Number(remainingDiscountPool);

    if (currentProgress >= 0 && currentProgress <= 100) {
      setProgress(Number(currentProgress.toFixed(2)));
    }

    setDiscount(percentage ?? 0);
  }, [discountCurve, product.pricing?.totalDiscountPercentage, setHasDiscount]);

  useEffect(() => {
    setShowdiscount(!notAvailable && (bondingCurve?.active || !!discountCurve));
  }, [notAvailable, bondingCurve, discountCurve]);

  const tradable = product.collectionType !== ProductCollectionType.WEB2;

  return (
    <CardFrame attributes={attributes}>
      <Card
        rc={<div />}
        sx={{
          cursor: 'auto',
          aspectRatio: 'initial',
          '& .textContainer': {
            position: 'initial',
            flex: 'auto'
          },
          '.innerTextContainer': {
            flex: 'auto'
          },
          height: '100%'
        }}
      >
        {product.image?.sourceUrl && (
          <Card.AssetImage
            sx={{
              pointerEvents: 'none',
              userSelect: 'none',
              aspectRatio: '9 / 5',
              minHeight: `${hasDiscount ? '180px !important' : ''}`,
              // maxHeight: `${hasDiscount ? '200px !important' : ''}`,
              width: '100%'
            }}
            aspectRatio="9:5"
            objectFit="cover"
            responsiveSizes={[640, 860, 1440] as ResponsiveSizes}
            relativeImageSizeInLayout={''}
            imageUrl={product.image.sourceUrl}
          />
        )}
        <Card.Caption
          sx={{
            position: 'absolute',
            top: 'base.spacing.x4',
            right: 'base.spacing.x4'
          }}
        >
          <StockStatus product={product} stockQty={stockQty} />
        </Card.Caption>
        <Card.Title>
          <Title product={product} tradable={tradable} />
        </Card.Title>
        <Card.Description
          sx={{
            flex: 'auto',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Description product={product} />
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              flexDirection: 'column',
              alignItems: 'flex-start',
              marginBottom: 'base.spacing.x4'
            }}
          >
            <StoreDiscount product={product} bundleSize={bundleSize} discount={discount} />
            <Pricing
              product={product}
              quantity={Math.floor(productQty / bundleSize)}
              bundleSize={bundleSize}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              flexDirection: 'row',
              justifyContent: 'flex-start',
              alignItems: 'flex-end'
            }}
          >
            <Quantity
              product={product}
              quantity={productQty}
              setQuantity={setProductQty}
              maxQuantity={availableBundle ? availableBundle : maxQuantity}
              notAvailable={notAvailable}
              bundleSize={bundleSize}
              exceedQuantity={exceedQuantity}
            />
            <BuyNow
              product={product}
              stockQty={stockQty}
              onClick={() => {
                onClick(product, productQty);
              }}
              notAvailable={notAvailable}
              isLimited={isLimited}
            />
          </Box>
          {!notAvailable ? (
            <WalletLimitInfomation
              onRefresh={refreshCountdown}
              limitQuantity={limitBundle != null ? `${limitBundle}` : undefined}
              availableQuantity={availableBundle != null ? `${availableBundle}` : undefined}
              countdownTime={countdown}
            />
          ) : null}
          {showDiscount ? (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  gap: 'base.spacing.x1'
                }}
              >
                Discount pool remaining
                <Box sx={{ color: 'base.color.accent.1' }}>{showDiscount ? progress : 0}%</Box>
              </Box>
              <Box>
                <ProgressBar
                  colorVariant="gradient"
                  variant="determinate"
                  size={'medium'}
                  value={progress}
                  sx={{
                    gap: '0',
                    width: '100%',
                    borderRadius: 'base.borderRadius.x5',
                    marginTop: 'base.spacing.x2',
                    backgroundColor: 'transparent',
                    border: '1px solid white'
                  }}
                >
                  <ProgressBar.Label text=""></ProgressBar.Label>
                  <ProgressBar.Caption></ProgressBar.Caption>
                </ProgressBar>
              </Box>
            </Box>
          ) : null}
        </Card.Description>
      </Card>
    </CardFrame>
  );
};
